import { UnboundTable } from '../../core/table'
import type { AnyColumn, WithFnBrand } from '../../core/table'
import type { Table } from '../../core/table'
import type { SqliteTableConfig, QueryContext } from '../../core/types'
import { sqliteColumns, type SqliteColumnFactory } from './columns'

/**
 * Creates a SQLite table definition with typed columns.
 *
 * Uses an object configuration pattern for clarity and extensibility.
 * Table names are derived from object keys when passed to createDb().
 *
 * @param config - Configuration object with columns, extensions, and indexes
 *
 * DDL execution order: CREATE TABLE → extensions → indexes
 *
 * @example
 * ```ts
 * const users = sqliteTable({
 *   columns: (c) => ({
 *     id: c.text().primaryKey(),  // SQLite uses TEXT for UUIDs
 *     name: c.text().notNull(),
 *     age: c.integer(),
 *   }),
 * })
 * const db = createDb({ users })  // table name: "users"
 *
 * // fn can reference sibling tables directly — closure captures the imported
 * // ref, which becomes bound when createDb runs. Use like the self `table`:
 * // `${teams}`, `${teams.col}`, `${teams.col.eq(x)}` all work.
 * // Not available on the imported ref: `.insert()`.
 * const users = sqliteTable({
 *   columns: (c) => ({ id: c.text().primaryKey(), teamId: c.text().notNull() }),
 *   fn: (table, db) => ({
 *     getWithTeam: (id: string) => db.query`
 *       SELECT ${table.id}, ${teams.name} AS "teamName"
 *       FROM ${table} JOIN ${teams} ON ${teams.id.eq(table.teamId)}
 *       WHERE ${table.id.eq(id)}
 *     `.executeOne(schema),
 *   }),
 * })
 * ```
 */
export function sqliteTable<
  Columns extends Record<string, AnyColumn>,
  // oxlint-disable-next-line typescript/no-explicit-any
  Fns = any,
>(
  config: SqliteTableConfig<Columns, SqliteColumnFactory> & {
    fn?: (table: Table<Columns> & Columns, db: QueryContext) => Fns
  },
): UnboundTable<Columns> & Columns & WithFnBrand<Fns> {
  const columns = applyGeneratedExpressions(
    config.columns(sqliteColumns),
    config.generated,
  )
  return new UnboundTable(
    columns,
    'sqlite',
    config.extensions,
    config.indexes,
    config.primaryKey,
    undefined,
    config.fn,
    config.schema,
    config.comment,
    undefined,
    config.__meta__,
    config.withoutRowId,
  ) as UnboundTable<Columns> & Columns & WithFnBrand<Fns>
}

export function defineSqliteColumns<Columns extends Record<string, AnyColumn>>(
  build: (columns: SqliteColumnFactory) => Columns,
): (columns: SqliteColumnFactory) => Columns {
  return build
}

function applyGeneratedExpressions<Columns extends Record<string, AnyColumn>>(
  columns: Columns,
  build: ((columns: Columns) => Record<string, unknown>) | undefined,
): Columns {
  if (!build) return columns
  const next = { ...columns }
  for (const [name, column] of Object.entries(next)) {
    if (!column.isGeneratedColumn) continue
    next[name as keyof Columns] = column._withGeneratedExpression((refs) => {
      const expression = build(refs as Columns)[name]
      if (!expression || typeof expression !== 'object') {
        throw new Error(`Generated column expression missing: ${name}`)
      }
      return expression as ReturnType<
        Parameters<typeof column._withGeneratedExpression>[0]
      >
    }) as Columns[keyof Columns]
  }
  return next
}
